import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Clipboard,
  KeyRound,
  RefreshCw,
  ShieldCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { FormEvent, useState } from "react";
import {
  fetchUsers,
  inviteUser,
  renewInvitation,
  updateUser,
} from "../lib/auth-api";
import { useI18n } from "../i18n";
import type { AuthUser, UserRole, UserStatus } from "../shared/auth";

export function AdminPanel({ currentUser }: { currentUser: AuthUser }) {
  const { text, locale } = useI18n();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<UserRole>("viewer");
  const [invitationUrl, setInvitationUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: fetchUsers,
  });
  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: (result) => {
      setInvitationUrl(result.invitationUrl);
      setEmail("");
      setDisplayName("");
      setRole("viewer");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({
      userId,
      input,
    }: {
      userId: string;
      input: { role?: UserRole; status?: UserStatus };
    }) => updateUser(userId, input),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const renewMutation = useMutation({
    mutationFn: renewInvitation,
    onSuccess: (result) => {
      setInvitationUrl(result.invitationUrl);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setInvitationUrl("");
    inviteMutation.mutate({ email, displayName, role });
  }

  async function copyInvitation() {
    await navigator.clipboard.writeText(invitationUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  return (
    <section className="admin-workspace">
      <div className="admin-summary">
        <div>
          <span className="eyebrow">{text.admin.accessControl}</span>
          <h2>{text.admin.authorizedUsers}</h2>
          <p>{text.admin.invitationsHelp}</p>
        </div>
        <div className="admin-count">
          <Users size={18} />
          <strong>{usersQuery.data?.users.length || 0}</strong>
          <span>{text.admin.users}</span>
        </div>
      </div>

      <div className="admin-layout">
        <article className="admin-section">
          <div className="admin-heading">
            <div>
              <strong>{text.admin.directory}</strong>
              <span>{text.admin.directoryMeta}</span>
            </div>
            <ShieldCheck size={18} />
          </div>

          {usersQuery.isLoading ? (
            <div className="admin-loading" />
          ) : (
            <div className="user-table-wrap">
              <table className="user-table">
                <thead>
                  <tr>
                    <th>{text.admin.user}</th>
                    <th>{text.admin.role}</th>
                    <th>{text.admin.status}</th>
                    <th>{text.admin.passkeys}</th>
                    <th>{text.admin.lastAccess}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {usersQuery.data?.users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <strong>{user.displayName}</strong>
                        <span>{user.email}</span>
                      </td>
                      <td>
                        <select
                          value={user.role}
                          disabled={user.id === currentUser.id}
                          onChange={(event) =>
                            updateMutation.mutate({
                              userId: user.id,
                              input: {
                                role: event.target.value as UserRole,
                              },
                            })
                          }
                        >
                          <option value="viewer">{text.admin.viewer}</option>
                          <option value="admin">{text.admin.admin}</option>
                        </select>
                      </td>
                      <td>
                        <span className={`user-status ${user.status}`}>
                          {statusLabel(user.status, text)}
                        </span>
                      </td>
                      <td>
                        <span className="passkey-count">
                          <KeyRound size={13} />
                          {user.passkeyCount}
                        </span>
                      </td>
                      <td>{formatDate(user.lastLoginAt, locale, text)}</td>
                      <td>
                        {user.status === "invited" ? (
                          <button
                            className="table-action"
                            title={text.admin.renewInvitation}
                            onClick={() => renewMutation.mutate(user.id)}
                          >
                            <RefreshCw size={15} />
                          </button>
                        ) : user.id !== currentUser.id ? (
                          <button
                            className="status-action"
                            onClick={() =>
                              updateMutation.mutate({
                                userId: user.id,
                                input: {
                                  status:
                                    user.status === "disabled"
                                      ? "active"
                                      : "disabled",
                                },
                              })
                            }
                          >
                            {user.status === "disabled"
                              ? text.admin.enable
                              : text.admin.disable}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>

        <article className="admin-section invite-section">
          <div className="admin-heading">
            <div>
              <strong>{text.admin.inviteUser}</strong>
              <span>{text.admin.inviteMeta}</span>
            </div>
            <UserPlus size={18} />
          </div>
          <form className="invite-form" onSubmit={submit}>
            <label>
              <span>{text.admin.name}</span>
              <input
                required
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder={text.admin.namePlaceholder}
              />
            </label>
            <label>
              <span>Email</span>
              <input
                required
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={text.admin.emailPlaceholder}
              />
            </label>
            <label>
              <span>{text.admin.role}</span>
              <select
                value={role}
                onChange={(event) =>
                  setRole(event.target.value as UserRole)
                }
              >
                <option value="viewer">{text.admin.viewer}</option>
                <option value="admin">{text.admin.admin}</option>
              </select>
            </label>
            <button
              className="secondary-button invite-button"
              disabled={inviteMutation.isPending}
            >
              <UserPlus size={15} />
              {inviteMutation.isPending
                ? text.admin.generating
                : text.admin.createInvitation}
            </button>
          </form>

          {invitationUrl ? (
            <div className="invitation-result">
              <span>{text.admin.oneTimeLink}</span>
              <div>
                <input readOnly value={invitationUrl} />
                <button
                  className="icon-button"
                  title={text.admin.copyInvitation}
                  onClick={copyInvitation}
                >
                  {copied ? <Check size={16} /> : <Clipboard size={16} />}
                </button>
              </div>
            </div>
          ) : null}

          {inviteMutation.isError ? (
            <div className="admin-error">{inviteMutation.error.message}</div>
          ) : null}
        </article>
      </div>
    </section>
  );
}

function statusLabel(status: UserStatus, text: ReturnType<typeof useI18n>["text"]) {
  if (status === "active") return text.admin.active;
  if (status === "disabled") return text.admin.disabled;
  return text.admin.invited;
}

function formatDate(
  value: string | null,
  locale: string,
  text: ReturnType<typeof useI18n>["text"],
) {
  if (!value) return text.admin.never;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
